import { useCallback, useMemo } from 'react';
import { useSetAtom } from 'jotai';
import useTranslation from 'next-translate/useTranslation';
import { toast } from '@flyer/components';
import * as Sentry from '@sentry/nextjs';
import { type RouteSkill } from '@/lib/routes';
import { ieltsAttemptErrorAtom } from '@/store/ieltsAttemptError';
import {
  type AttemptErrorPhase,
  type AttemptUserError,
  type ResolvedAttemptError,
  resolveAttemptError,
  resolveUserError,
} from '@/lib/ielts-attempt-error';

/** Shared toast id so a flaky connection cannot stack duplicate toasts. */
const ATTEMPT_ERROR_TOAST_ID = 'ielts-attempt-error';

interface UseIeltsAttemptErrorOptions {
  skill: RouteSkill;
  /** Needed for the "view result" way out; absent before the attempt starts. */
  attemptId?: string | null;
  /** Where `back` / `restart` should land. Defaults to the skill's test list. */
  backHref?: string;
}

/**
 * Turns an Apollo error from an IELTS attempt mutation into learner-facing UI.
 *
 * Callers pass the raw error plus the phase it came from; this decides between a
 * toast and a blocking modal, translates the copy, and reports admin/backend
 * faults to Sentry. Blocking errors go into `ieltsAttemptErrorAtom`, which the
 * `IeltsAttemptErrorModals` mounted in `_app` renders.
 *
 * Returns the resolved error so callers can branch on it — for instance to skip
 * a redirect that the modal's own action is about to perform.
 */
export default function useIeltsAttemptError({
  skill,
  attemptId,
  backHref,
}: UseIeltsAttemptErrorOptions) {
  const { t } = useTranslation('ielts');
  const setAttemptError = useSetAtom(ieltsAttemptErrorAtom);

  const present = useCallback(
    (resolved: ResolvedAttemptError, phase: AttemptErrorPhase, cause?: unknown) => {
      if (resolved.report) {
        Sentry.withScope((scope) => {
          scope.setTag('ielts_attempt_error_code', resolved.code);
          scope.setTag('ielts_attempt_error_phase', phase);
          scope.setExtras({ skill, attemptId });
          Sentry.captureException(
            cause instanceof Error ? cause : new Error(`IELTS attempt error: ${resolved.code}`),
          );
        });
      }

      if (resolved.kind === 'modal') {
        setAttemptError({
          i18nKey: resolved.i18nKey,
          action: resolved.action,
          skill,
          attemptId: attemptId || undefined,
          backHref,
        });
        return;
      }

      toast.error(t(`error.attempt.${resolved.i18nKey}.message`), {
        id: ATTEMPT_ERROR_TOAST_ID,
      });
    },
    [attemptId, backHref, setAttemptError, skill, t],
  );

  /** For the `errors[]` channel — call from Apollo's `onError` or a `catch`. */
  const handleAttemptError = useCallback(
    (error: unknown, phase: AttemptErrorPhase): ResolvedAttemptError => {
      const resolved = resolveAttemptError(error, phase);
      present(resolved, phase, error);
      return resolved;
    },
    [present],
  );

  /**
   * For the `payload.userErrors` channel — call from `onCompleted` before acting
   * on the result. Returns true when an error was shown, meaning the caller must
   * stop and not treat the mutation as successful.
   */
  const handleUserErrors = useCallback(
    (
      userErrors: readonly AttemptUserError[] | null | undefined,
      phase: AttemptErrorPhase,
    ): boolean => {
      const resolved = resolveUserError(userErrors, phase);
      if (!resolved) {
        return false;
      }
      present(resolved, phase);
      return true;
    },
    [present],
  );

  return useMemo(
    () => ({ handleAttemptError, handleUserErrors }),
    [handleAttemptError, handleUserErrors],
  );
}
